import React from "react";
import {
  ArrowRight,
  Banknote,
  Building2,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  FileText,
  Info,
  Landmark,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

import "./RenovationProjectFinancingStep.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/* =========================================================
   STEP
   ========================================================= */

function findFinancingStep(steps = []) {
  return (
    steps.find((step) => step.step_code === "FINANCING") || {
      step_code: "FINANCING",
      step_order: 7,
      status: "NOT_STARTED",
      visible_to: "HB_OSS",
      editable_by: "SYSTEM",
    }
  );
}

/* =========================================================
   HELPERS
   ========================================================= */

function normalizeStatusClass(value) {
  return String(value || "NOT_STARTED")
    .trim()
    .toLowerCase()
    .replaceAll("_", "-");
}

function formatCurrency(value, currency = "EUR", locale = "en") {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  try {
    return new Intl.NumberFormat(locale || "en", {
      style: "currency",
      currency: currency || "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parsed);
  } catch {
    return `${parsed.toFixed(2)} ${currency || "EUR"}`;
  }
}

function formatDate(value, locale = "en") {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  try {
    return new Intl.DateTimeFormat(locale || "en", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch {
    return value;
  }
}

/* =========================================================
   MATURITY PRESENTATION
   ========================================================= */

function getMaturityPresentation(maturity, t) {
  const normalized = String(maturity || "PENDING").toUpperCase();

  if (normalized === "CONFIRMED") {
    return {
      code: "CONFIRMED",
      icon: CheckCircle2,
      label: t("maturity.CONFIRMED.label", "Confirmed"),
      title: t("maturity.CONFIRMED.title", "Renovation financing confirmed"),
      description: t(
        "maturity.CONFIRMED.description",
        "The lender has confirmed the renovation financing amount for this project.",
      ),
    };
  }

  if (normalized === "ESTIMATED") {
    return {
      code: "ESTIMATED",
      icon: Clock3,
      label: t("maturity.ESTIMATED.label", "Estimated"),
      title: t("maturity.ESTIMATED.title", "Renovation financing estimated"),
      description: t(
        "maturity.ESTIMATED.description",
        "The lender has approved renovation financing, but the final amount is still estimated.",
      ),
    };
  }

  if (normalized === "NOT_GRANTED") {
    return {
      code: "NOT_GRANTED",
      icon: XCircle,
      label: t("maturity.NOT_GRANTED.label", "Not granted"),
      title: t(
        "maturity.NOT_GRANTED.title",
        "Renovation financing not granted",
      ),
      description: t(
        "maturity.NOT_GRANTED.description",
        "No lender renovation financing has been granted for this project.",
      ),
    };
  }

  return {
    code: "PENDING",
    icon: Clock3,
    label: t("maturity.PENDING.label", "Pending"),
    title: t("maturity.PENDING.title", "Financing decision pending"),
    description: t(
      "maturity.PENDING.description",
      "The final lender financing decision is not available yet.",
    ),
  };
}

/* =========================================================
   COMPONENT
   ========================================================= */

export default function RenovationProjectFinancingStep() {
  const { t, i18n } = useTranslation("renovation-project-financing");

  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth0();

  const { project, steps, basePath, refreshProject } = useOutletContext();

  const step = React.useMemo(() => findFinancingStep(steps), [steps]);

  const [financing, setFinancing] = React.useState(null);

  const [loading, setLoading] = React.useState(true);

  const [loadError, setLoadError] = React.useState("");

  const [refreshing, setRefreshing] = React.useState(false);

  /* =======================================================
     LOAD FINANCING
     ======================================================= */

  const loadFinancing = React.useCallback(
    async ({ manual = false } = {}) => {
      if (!project?.id) {
        return;
      }

      if (manual) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setLoadError("");

      try {
        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${API_BASE}/api/projects/${project.id}/financing/`,
          {
            method: "GET",

            headers: {
              Authorization: `Bearer ${token}`,

              Accept: "application/json",
            },
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail ||
              t("errors.loadFailed", "Project financing could not be loaded."),
          );
        }

        setFinancing(data);

        /*
         * The GET endpoint synchronises the persisted
         * FINANCING workflow step. Refresh the shared
         * project so the sidebar/progress immediately
         * receives the canonical backend status.
         */
        await refreshProject?.();
      } catch (error) {
        console.error("Failed to load project financing:", error);

        setLoadError(
          error?.message ||
            t("errors.loadFailed", "Project financing could not be loaded."),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [getAccessTokenSilently, project?.id, refreshProject, t],
  );

  React.useEffect(() => {
    loadFinancing();
  }, [loadFinancing]);

  /* =======================================================
     DISPLAY STATE
     ======================================================= */

  const displayStepStatus =
    financing?.step_status || step?.status || "NOT_STARTED";

  const maturity = financing?.financing_maturity || "PENDING";

  const presentation = React.useMemo(
    () => getMaturityPresentation(maturity, t),
    [maturity, t],
  );

  const MaturityIcon = presentation.icon;

  const formattedAmount = formatCurrency(
    financing?.renovation_financing_amount,
    financing?.currency || "EUR",
    i18n?.language || "en",
  );

  const formattedConfirmedAt = formatDate(
    financing?.confirmed_at,
    i18n?.language || "en",
  );

  const lenderName = financing?.lender?.name || "";

  const loanReference = financing?.loan_case?.reference || "";

  const hasAmount = Boolean(formattedAmount);

  const isEstimated = maturity === "ESTIMATED";

  const isConfirmed = maturity === "CONFIRMED";

  const isNotGranted = maturity === "NOT_GRANTED";

  const isPending = maturity === "PENDING";

  /* =======================================================
     NAVIGATION
     ======================================================= */

  const goNext = () => {
    navigate(`${basePath}/${project.id}/monitoring`);
  };

  /* =======================================================
     LOADING
     ======================================================= */

  if (loading) {
    return (
      <article className="financing-step financing-step--loading">
        <div className="financing-loading">
          <span className="financing-loading__icon">
            <LoaderCircle size={26} />
          </span>

          <div>
            <strong>{t("loading.title", "Loading financing")}</strong>

            <span>
              {t(
                "loading.description",
                "Synchronising the latest lender financing information...",
              )}
            </span>
          </div>
        </div>
      </article>
    );
  }

  /* =======================================================
     ERROR
     ======================================================= */

  if (loadError) {
    return (
      <article className="financing-step">
        <div className="financing-error">
          <span className="financing-error__icon">
            <Info size={24} />
          </span>

          <div className="financing-error__content">
            <strong>
              {t("errors.title", "Financing information could not be loaded")}
            </strong>

            <p>{loadError}</p>
          </div>

          <button
            type="button"
            className="financing-retry"
            onClick={() =>
              loadFinancing({
                manual: true,
              })
            }
            disabled={refreshing}
          >
            <RefreshCw size={16} />

            {refreshing
              ? t("buttons.refreshing", "Refreshing...")
              : t("buttons.retry", "Try again")}
          </button>
        </div>
      </article>
    );
  }

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <article className="financing-step">
      {/* =================================================
          HEADER
          ================================================= */}

      <header className="financing-step__header">
        <div className="financing-step__heading">
          <span className="financing-step__eyebrow">
            <CircleDollarSign size={15} />

            {t("header.step", "Step 7")}
          </span>

          <div className="financing-step__title-row">
            <h1>{t("header.title", "Financing")}</h1>

            <span className="financing-step__scope">
              <ShieldCheck size={14} />

              {t("header.systemManaged", "System managed")}
            </span>
          </div>

          <p>
            {t(
              "header.subtitle",
              "Review the renovation financing provided through the lender decision.",
            )}
          </p>
        </div>

        <div className="financing-step__status">
          <span className="financing-step__stage-count">
            {t("header.stage", "Stage {{current}} of {{total}}", {
              current: step?.step_order || 7,

              total: 8,
            })}
          </span>

          <span
            className={
              `financing-step__status-pill ` +
              `financing-step__status-pill--${normalizeStatusClass(
                displayStepStatus,
              )}`
            }
          >
            {t(
              `stepStatuses.${displayStepStatus}`,
              String(displayStepStatus).replaceAll("_", " ").toLowerCase(),
            )}
          </span>
        </div>
      </header>

      {/* =================================================
          SYSTEM NOTE
          ================================================= */}

      <div className="financing-system-note">
        <ShieldCheck size={18} />

        <div>
          <strong>
            {t("systemNote.title", "Financing is updated automatically.")}
          </strong>

          <span>
            {t(
              "systemNote.description",
              "The financing status and amount come from the lender's final loan decision. Homebuyers and OSS representatives cannot edit them from this stage.",
            )}
          </span>
        </div>
      </div>

      {/* =================================================
          MAIN FINANCING SECTION
          ================================================= */}

      <section
        className={
          `financing-section ` +
          `financing-section--${normalizeStatusClass(maturity)}`
        }
      >
        <div className="financing-section__header">
          <div className="financing-section__heading">
            <span className="financing-section__icon">
              <Banknote size={21} />
            </span>

            <div>
              <h2>{t("summary.title", "Renovation financing")}</h2>

              <p>
                {t(
                  "summary.subtitle",
                  "Current lender-backed financing position for the renovation works.",
                )}
              </p>
            </div>
          </div>

          <span
            className={
              `financing-maturity-pill ` +
              `financing-maturity-pill--${normalizeStatusClass(maturity)}`
            }
          >
            <MaturityIcon size={15} />

            {presentation.label}
          </span>
        </div>

        {/* ===============================================
            HERO STATE
            =============================================== */}

        <div className="financing-summary-card">
          <div
            className={
              `financing-summary-card__state ` +
              `financing-summary-card__state--${normalizeStatusClass(maturity)}`
            }
          >
            <span className="financing-summary-card__state-icon">
              <MaturityIcon size={24} />
            </span>

            <div>
              <strong>{presentation.title}</strong>

              <p>{presentation.description}</p>
            </div>
          </div>

          {/* =============================================
              AMOUNT
              ============================================= */}

          <div className="financing-amount-panel">
            <span className="financing-amount-panel__label">
              {isEstimated
                ? t("amount.estimatedLabel", "Estimated renovation financing")
                : isConfirmed
                  ? t("amount.confirmedLabel", "Confirmed renovation financing")
                  : isNotGranted
                    ? t("amount.notGrantedLabel", "Renovation financing")
                    : t("amount.pendingLabel", "Renovation financing")}
            </span>

            {hasAmount && !isNotGranted ? (
              <strong className="financing-amount-panel__value">
                {formattedAmount}
              </strong>
            ) : isNotGranted ? (
              <strong className="financing-amount-panel__value financing-amount-panel__value--muted">
                {t("amount.notGrantedValue", "Not granted")}
              </strong>
            ) : (
              <strong className="financing-amount-panel__value financing-amount-panel__value--muted">
                {t("amount.pendingValue", "Pending")}
              </strong>
            )}

            <span className="financing-amount-panel__caption">
              {isEstimated
                ? t(
                    "amount.estimatedCaption",
                    "The amount may still change until the lender confirms the final renovation financing.",
                  )
                : isConfirmed
                  ? t(
                      "amount.confirmedCaption",
                      "This is the lender-confirmed amount available for the renovation.",
                    )
                  : isNotGranted
                    ? t(
                        "amount.notGrantedCaption",
                        "The final lender decision does not include renovation financing.",
                      )
                    : t(
                        "amount.pendingCaption",
                        "The renovation financing amount will appear after a final lender decision is available.",
                      )}
            </span>
          </div>
        </div>

        {/* ===============================================
            DETAILS
            =============================================== */}

        <div className="financing-details-grid">
          <div className="financing-detail-card">
            <span className="financing-detail-card__icon">
              <Building2 size={18} />
            </span>

            <div>
              <span>{t("details.lender", "Lender")}</span>

              <strong>
                {lenderName || t("details.notAvailable", "Not available")}
              </strong>
            </div>
          </div>

          <div className="financing-detail-card">
            <span className="financing-detail-card__icon">
              <FileText size={18} />
            </span>

            <div>
              <span>{t("details.loanReference", "Loan case reference")}</span>

              <strong>
                {loanReference || t("details.notAvailable", "Not available")}
              </strong>
            </div>
          </div>

          <div className="financing-detail-card">
            <span className="financing-detail-card__icon">
              <Landmark size={18} />
            </span>

            <div>
              <span>{t("details.decision", "Lender decision")}</span>

              <strong>
                {financing?.decision
                  ? t(`decisions.${financing.decision}`, financing.decision)
                  : t("decisions.PENDING", "Pending")}
              </strong>
            </div>
          </div>

          <div className="financing-detail-card">
            <span className="financing-detail-card__icon">
              <CalendarDays size={18} />
            </span>

            <div>
              <span>{t("details.confirmedAt", "Confirmation date")}</span>

              <strong>
                {formattedConfirmedAt ||
                  (isEstimated
                    ? t("details.awaitingConfirmation", "Awaiting confirmation")
                    : t("details.notAvailable", "Not available"))}
              </strong>
            </div>
          </div>
        </div>

        {/* ===============================================
            NOTES
            =============================================== */}

        {financing?.notes ? (
          <div className="financing-notes">
            <Info size={18} />

            <div>
              <strong>{t("notes.title", "Financing notes")}</strong>

              <p>{financing.notes}</p>
            </div>
          </div>
        ) : null}

        {/* ===============================================
            STATE-SPECIFIC INFORMATION
            =============================================== */}

        {isEstimated ? (
          <div className="financing-state-info financing-state-info--estimated">
            <Clock3 size={18} />

            <div>
              <strong>
                {t(
                  "stateInfo.estimated.title",
                  "The financing is approved but not yet final.",
                )}
              </strong>

              <span>
                {t(
                  "stateInfo.estimated.description",
                  "The lender can confirm the renovation financing after the full renovation plan and final financing needs have been assessed. Until then, Step 7 remains in progress.",
                )}
              </span>
            </div>
          </div>
        ) : null}

        {isConfirmed ? (
          <div className="financing-state-info financing-state-info--confirmed">
            <CheckCircle2 size={18} />

            <div>
              <strong>
                {t(
                  "stateInfo.confirmed.title",
                  "The renovation financing has been confirmed.",
                )}
              </strong>

              <span>
                {t(
                  "stateInfo.confirmed.description",
                  "The lender-confirmed renovation financing is now reflected in the project and this stage is completed.",
                )}
              </span>
            </div>
          </div>
        ) : null}

        {isNotGranted ? (
          <div className="financing-state-info financing-state-info--not-granted">
            <XCircle size={18} />

            <div>
              <strong>
                {t(
                  "stateInfo.notGranted.title",
                  "No renovation financing has been granted.",
                )}
              </strong>

              <span>
                {t(
                  "stateInfo.notGranted.description",
                  "The lender decision is final, so this financing stage is considered completed even though no renovation financing was granted.",
                )}
              </span>
            </div>
          </div>
        ) : null}

        {isPending ? (
          <div className="financing-state-info financing-state-info--pending">
            <Clock3 size={18} />

            <div>
              <strong>
                {t(
                  "stateInfo.pending.title",
                  "Waiting for the lender decision.",
                )}
              </strong>

              <span>
                {t(
                  "stateInfo.pending.description",
                  "This stage will update automatically once the lender records a final financing decision.",
                )}
              </span>
            </div>
          </div>
        ) : null}
      </section>

      {/* =================================================
          REFRESH
          ================================================= */}

      <div className="financing-refresh-row">
        <button
          type="button"
          className="financing-refresh"
          onClick={() =>
            loadFinancing({
              manual: true,
            })
          }
          disabled={refreshing}
        >
          <RefreshCw size={15} />

          {refreshing
            ? t("buttons.refreshing", "Refreshing...")
            : t("buttons.refresh", "Refresh financing status")}
        </button>
      </div>

      {/* =================================================
          NEXT
          ================================================= */}

      <button type="button" className="financing-next" onClick={goNext}>
        <span>{t("buttons.continue", "Continue to Monitoring")}</span>

        <ArrowRight size={18} />
      </button>
    </article>
  );
}
